import React, { useMemo } from 'react';
import { UserAccountSummary } from '~domain/model/account/user-account-summary';
import { Carrier } from '~domain/model/core/carrier';
import { HomeLine } from '~domain/model/core/home-line';
import { ProductType } from '~domain/model/core/product-type';
import { ExternalLink, Img } from '~frontend-common/components/ui';
import { useTranslator } from '~frontend-common/i18n/translator';
import { CommonImages } from '~frontend-common/images';
import { Box } from '~ui-kit/components/box/box';
import { Card } from '~ui-kit/components/card/card';
import { Divider } from '~ui-kit/components/divider';
import { Typography } from '~ui-kit/components/typography/typography';
import { styled } from '~ui-kit/styled-components';
import { theme } from '~ui-kit/theme';
import { ActionType, PolicyModificationConfig } from '../models/policy-modification-config';
import { TypeFormButton } from './type-form-button';

interface PolicyModificationCardProps {
  title: string;
  desc: string;
  config: PolicyModificationConfig;
  user: UserAccountSummary;
  policyInfo: { policyNumber: string; productType: ProductType; homeLine?: HomeLine; carrier: Carrier };
}

export function PolicyModificationCard({ title, desc, config, user, policyInfo }: PolicyModificationCardProps) {
  const translator = useTranslator();
  const currentLanguageId = translator.getCurrentLanguageId();
  const iconSrc = CommonImages.policies.modificationOptions[config.type];

  const hiddenFields: Record<string, string> = useMemo(
    () => ({
      user_id: user?.id,
      user_close_lead_id: user?.closeLeadId,
      user_email: user?.email,
      user_first_name: user?.firstName,
      user_last_name: user?.lastName,
      policy_product_type: policyInfo.productType,
      policy_home_line: policyInfo.homeLine,
      policy_number: policyInfo.policyNumber,
      policy_carrier: policyInfo.carrier,
    }),
    [user],
  );

  const content = (
    <StyledCard role="button">
      <Box alignItems="center">
        <Img src={iconSrc} height="48px" width="48px" />
        <Typography $ml={2} $fontSize="18px" $lineHeight="18px" $textAlign="left" $color={theme.colors.black}>
          {title}
        </Typography>
      </Box>

      <Divider $my={3} />

      <Typography $display="block" $fontSize="14px" $lineHeight="18px" $textAlign="left" $color={theme.colors.black}>
        {desc}
      </Typography>
    </StyledCard>
  );

  if (config.actionType === ActionType.Redirection) {
    return (
      <StyledExternalLink
        href={config.linkOptions?.urlByLang[currentLanguageId]}
        openNewTab={config.linkOptions?.openNewTab}
        color={theme.colors.black}
        colorHover={theme.colors.black}
      >
        {content}
      </StyledExternalLink>
    );
  }

  if (config.actionType === ActionType.Typeform) {
    return (
      <TypeFormButton typeformId={config.typeformIds?.[currentLanguageId]} typeformHiddenFields={hiddenFields}>
        {content}
      </TypeFormButton>
    );
  }
}

const StyledExternalLink = styled(ExternalLink)`
  && {
    display: flex;
    height: 100%;
    width: 100%;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  cursor: pointer;
  color: ${(props) => props.color};

  &:hover {
    color: ${(props) => props.color};
    background: ${(props) => props.theme.colors.greyBackground};
  }
`;
